import React, { useState } from 'react';
import { Grid, Card, CardContent, Typography, Box, Chip, Stack, Divider, Alert, Button, CircularProgress } from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import SecurityIcon from '@mui/icons-material/Security';
import ExtensionIcon from '@mui/icons-material/Extension';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RefreshIcon from '@mui/icons-material/Refresh';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';

const Field = ({ label, value }) => (
  <Box sx={{ mb: 1.25 }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ wordBreak: 'break-all', fontWeight: 500 }}>
      {value || <span style={{ color: '#a0aec0' }}>—</span>}
    </Typography>
  </Box>
);

const InfoCard = ({ icon, title, children }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, color: 'primary.main' }}>
        {icon}
        <Typography variant="h6" color="text.primary">{title}</Typography>
      </Stack>
      <Divider sx={{ mb: 2 }} />
      {children}
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { user, entitlements, tokenExpiresAt, productId, canonicalPermissions, productPermissions, refreshAccessToken } = useAuth();
  const roles = entitlements?.roles || user?.roles || [];
  const expiry = tokenExpiresAt ? new Date(tokenExpiresAt).toLocaleString() : null;
  const productModules = Object.keys(productPermissions || {});

  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const handleRefreshToken = async () => {
    setRefreshing(true);
    setRefreshError(null);
    try {
      await refreshAccessToken();
      setRefreshedAt(new Date().toLocaleTimeString());
    } catch (err) {
      setRefreshError(err.response?.data?.message || err.message || 'Could not refresh the token');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title={`Welcome${user?.name ? `, ${user.name.split(' ')[0]}` : ''}`}
        subtitle="Your current session, resolved through the SSO-Gateway."
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <InfoCard icon={<BadgeIcon />} title="Identity">
            <Field label="Name" value={user?.name} />
            <Field label="Email" value={user?.email} />
            <Field label="User ID (sub)" value={user?.user_id} />
          </InfoCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoCard icon={<BusinessIcon />} title="Context">
            <Field label="Organization ID (token)" value={user?.organization_id} />
            <Field label="Local org id (entitlement key)" value={user?.local_organization_id} />
            <Field label="Product ID" value={productId} />
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Roles
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {roles.length ? roles.map((r) => <Chip key={r} label={r} color="primary" size="small" />) : <Typography variant="body2" color="text.disabled">none</Typography>}
              </Stack>
            </Box>
          </InfoCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoCard icon={<SecurityIcon />} title="Canonical permissions">
            {canonicalPermissions.length ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {canonicalPermissions.map((p) => (
                  <Chip key={p} label={p} variant="outlined" size="small" sx={{ fontFamily: 'monospace' }} />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.disabled">No canonical permissions resolved.</Typography>
            )}
          </InfoCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoCard icon={<ExtensionIcon />} title="Product permissions">
            {productModules.length ? (
              <Stack spacing={1.5}>
                {productModules.map((mod) => (
                  <Box key={mod}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {mod}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {(productPermissions[mod] || []).map((p) => (
                        <Chip key={`${mod}:${p}`} label={p} variant="outlined" size="small" color="secondary" sx={{ fontFamily: 'monospace' }} />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.disabled">No product permissions resolved.</Typography>
            )}
          </InfoCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoCard icon={<ScheduleIcon />} title="Access token">
            <Field label="Expires at" value={expiry} />
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.5, mb: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                onClick={handleRefreshToken}
                disabled={refreshing}
              >
                {refreshing ? 'Refreshing…' : 'Request new token'}
              </Button>
              {refreshedAt && !refreshing && !refreshError && (
                <Typography variant="caption" color="success.main">Refreshed at {refreshedAt}</Typography>
              )}
            </Stack>
            {refreshError && <Alert severity="error" sx={{ mb: 1 }}>{refreshError}</Alert>}
            <Alert severity="info" sx={{ mt: 1 }}>
              Tokens are held server-side by the BFF. Your browser only holds a session cookie.
              “Request new token” exchanges the refresh token for a fresh access token.
            </Alert>
          </InfoCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
